import Quill from 'quill';

const Embed: any = Quill.import('blots/embed');
export const Delta: any = Quill.import('delta');

export const VARIABLE_CLASS = 'ql-variable';
export const VARIABLE_ATTRIBUTE = 'data-variable';
export const VARIABLE_SELECTOR = `span.${VARIABLE_CLASS}`;

export const VARIABLE_PATTERN_SOURCE = '\\{\\{\\s*([^{}]*?)\\s*\\}\\}';

export function variablePattern(): RegExp {
  return new RegExp(VARIABLE_PATTERN_SOURCE, 'g');
}

const GUARD_TEXT = /\uFEFF/g;

// Chrome types U+00A0 for a space at a text-node boundary, which is where a pill's guard sits. Only a lone
// one beside a token is normalised: runs of them are deliberate spacing.
const NBSP_AFTER_TOKEN = new RegExp(`(${VARIABLE_PATTERN_SOURCE})\\u00A0(?!\\u00A0)`, 'g');
const NBSP_BEFORE_TOKEN = new RegExp(`(^|[^\\u00A0])\\u00A0(${VARIABLE_PATTERN_SOURCE})`, 'g');

const UNKNOWN_CLASS = `${VARIABLE_CLASS}--unknown`;

// Each editor permits its own names, and Quill re-runs create(value) on undo, redo and paste
// without saying which editor it is in, so the badge decision travels in the embed value.
export interface VariableValue {
  name: string;
  unknown: boolean;
}

type ClipboardMatcher = (node: Node, delta: any) => any;

export default class VariableBlot extends Embed {
  static blotName = 'variable';
  static tagName = 'span';
  static className = VARIABLE_CLASS;

  // The label must stay a real text node: removeTrailingBreaks/isEditorEmpty and
  // the serializer rely on it, and CSS ::before content would be invisible to them.
  static create(value: VariableValue): HTMLElement {
    const node = super.create(value) as HTMLElement;
    node.setAttribute(VARIABLE_ATTRIBUTE, value.name);
    node.textContent = `{{${value.name}}}`;

    if (value.unknown) {
      node.classList.add(UNKNOWN_CLASS);
      node.setAttribute('title', 'Unknown variable');
    }
    return node;
  }

  static value(node: HTMLElement): VariableValue {
    return { name: node.getAttribute(VARIABLE_ATTRIBUTE) ?? '', unknown: node.classList.contains(UNKNOWN_CLASS) };
  }

  // Chrome rewrites the guard more than once when a space is typed into it. Embed.restore consumes the
  // typed text on the first record, so a repeat finds the guard empty and moves the caret back to the
  // start of the lifted text, in front of the space.
  update(mutations: MutationRecord[], context: Record<string, unknown>): void {
    const restored = new Set<Node>();
    const firstPerGuard = mutations.filter((mutation) => {
      if (mutation.type !== 'characterData') return true;
      const guard = mutation.target === this.leftGuard || mutation.target === this.rightGuard;
      if (!guard) return true;
      if (restored.has(mutation.target)) return false;
      restored.add(mutation.target);
      return true;
    });
    super.update(firstPerGuard, context);
  }
}

export function variableValue(name: string, known: Set<string>): VariableValue {
  return { name, unknown: !known.has(name) };
}

export function variableMatchers(known: Set<string>): [number | string, ClipboardMatcher][] {
  return [
    [Node.TEXT_NODE, variableTextMatcher(known)],
    // Pill markup copied from another editor arrives carrying that editor's badge.
    [VARIABLE_SELECTOR, (_node: Node, delta: any) => rebadge(delta, known)]
  ];
}

function variableTextMatcher(known: Set<string>): ClipboardMatcher {
  return (_node: Node, delta: any) => {
    const ops: any[] = delta.ops;
    if (ops.some((op: any) => typeof op.insert !== 'string')) return delta;

    return variableDelta(ops.map((op: any) => op.insert).join(''), known) ?? delta;
  };
}

function rebadge(delta: any, known: Set<string>): any {
  return new Delta(delta.ops.map((op: any) => {
    const value: VariableValue | undefined = op.insert?.variable;
    return value ? { ...op, insert: { variable: variableValue(value.name, known) } } : op;
  }));
}

// null when the text holds no variable, so the caller keeps its own delta untouched.
export function variableDelta(text: string, known: Set<string>, attributes?: Record<string, unknown>): any | null {
  const pattern = variablePattern();
  const result = new Delta();
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) result.insert(text.slice(cursor, match.index), attributes);
    result.insert({ variable: variableValue(match[1].trim(), known) }, attributes);
    cursor = match.index + match[0].length;
  }

  if (cursor === 0) return null;
  if (cursor < text.length) result.insert(text.slice(cursor), attributes);
  return result;
}

// Stands in for an embed in line text: it takes one index like the embed does, but is not a brace, so a
// match containing one is dropped rather than letting a token swallow a pill or a line break.
export const EMBED_PLACEHOLDER = '\uFFFC';

export interface VariableToken {
  index: number;
  length: number;
  name: string;
}

export function variableTokensIn(lineText: string): VariableToken[] {
  const pattern = variablePattern();
  const tokens: VariableToken[] = [];
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(lineText)) !== null) {
    if (match[0].includes(EMBED_PLACEHOLDER)) continue;
    tokens.push({ index: match.index, length: match[0].length, name: match[1].trim() });
  }
  return tokens;
}

export function serializeVariables(root: Element): string {
  const clone = root.cloneNode(true) as Element;

  clone.querySelectorAll(VARIABLE_SELECTOR).forEach((pill) => {
    const name = pill.getAttribute(VARIABLE_ATTRIBUTE);
    const text = name !== null ? `{{${name}}}` : (pill.textContent ?? '');
    pill.replaceWith(document.createTextNode(text));
  });

  clone.normalize();
  const walker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
    node.data = node.data
      .replace(GUARD_TEXT, '')
      .replace(NBSP_AFTER_TOKEN, '$1 ')
      .replace(NBSP_BEFORE_TOKEN, '$1 $2');
  }

  return clone.innerHTML;
}

export function isPillRightGuard(node: Node | null | undefined): node is Text {
  const pill = node?.parentElement;
  return node?.nodeType === Node.TEXT_NODE && !!pill?.classList.contains(VARIABLE_CLASS) && pill.lastChild === node;
}
